import React, { useEffect, useRef, useState } from "react";

const cache = new Map();
const BINS = 800;

async function loadPeaks(src) {
  if (cache.has(src)) return cache.get(src);
  const p = (async () => {
    const url = src + (src.includes("?") ? "&" : "?") + "count=false";
    const buf = await fetch(url).then((r) => r.arrayBuffer());
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const audio = await ctx.decodeAudioData(buf);
    ctx.close?.();
    const ch = audio.getChannelData(0);
    const step = Math.floor(ch.length / BINS) || 1;
    const peaks = new Float32Array(BINS);
    let max = 0;
    for (let i = 0; i < BINS; i++) {
      let m = 0;
      const start = i * step;
      for (let j = start; j < start + step && j < ch.length; j += 4) {
        const v = Math.abs(ch[j]);
        if (v > m) m = v;
      }
      peaks[i] = m;
      if (m > max) max = m;
    }
    if (max > 0) for (let i = 0; i < BINS; i++) peaks[i] /= max;
    return peaks;
  })();
  cache.set(src, p);
  p.catch(() => cache.delete(src));
  return p;
}

export function Waveform({ src, progress, duration, onSeek, previewSec, previewStart = 0, className = "" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [peaks, setPeaks] = useState(null);
  const [hoverX, setHoverX] = useState(null);

  useEffect(() => {
    setPeaks(null);
    let alive = true;
    loadPeaks(src).then((pk) => alive && setPeaks(pk)).catch(() => {});
    return () => { alive = false; };
  }, [src]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const W = wrap.clientWidth, H = wrap.clientHeight;
      if (!W || !H) return;
      canvas.width = W * dpr; canvas.height = H * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, W, H);
      const barW = 2, gap = 1, n = Math.floor(W / (barW + gap));
      const ratio = duration ? progress / duration : 0;
      const limit = previewSec && duration ? (previewStart + previewSec) / duration : 1;
      const lower = previewSec && duration ? previewStart / duration : 0;
      const mid = H / 2;
      for (let i = 0; i < n; i++) {
        const x = i * (barW + gap);
        const frac = i / n;
        const v = peaks ? peaks[Math.floor(frac * BINS)] : 0.12 + 0.08 * Math.sin(i * 0.35);
        const h = Math.max(2, v * (H - 4));
        if (frac <= ratio) ctx.fillStyle = "#E50914";
        else if (frac > limit || frac < lower) ctx.fillStyle = "rgba(255,255,255,0.08)";
        else ctx.fillStyle = peaks ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.15)";
        ctx.fillRect(x, mid - h / 2, barW, h);
      }
      if (hoverX !== null) {
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fillRect(hoverX, 0, 1, H);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [peaks, progress, duration, previewSec, previewStart, hoverX]);

  const seekAt = (e) => {
    if (!Number.isFinite(duration) || duration <= 0) return;
    const r = wrapRef.current.getBoundingClientRect();
    onSeek(Math.min(duration, Math.max(0, ((e.clientX - r.left) / r.width) * duration)));
  };

  return (
    <div ref={wrapRef} data-testid="player-waveform"
      className={`relative w-full h-full cursor-pointer ${className}`}
      onClick={seekAt}
      onMouseMove={(e) => setHoverX(e.clientX - wrapRef.current.getBoundingClientRect().left)}
      onMouseLeave={() => setHoverX(null)}>
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
